import React, { useState } from 'react';
import { flexRender, Header, Table } from '@tanstack/react-table';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ColumnFilter from './ColumnFilter';
import { TanstackTableColumnFacet } from './types';
import { useTanstackTableLocale } from './locale';
import { getColumnSizingStyle, getPinningStyles, toFlexJustify, toMuiAlign } from './utils';

interface TanstackTableHeaderCellProps<TData> {
  header: Header<TData, unknown>;
  table: Table<TData>;
  facets?: Record<string, TanstackTableColumnFacet>;
  hasGrowColumn?: boolean;
}

function TanstackTableHeaderCell<TData>({
  header,
  table,
  facets,
  hasGrowColumn,
}: TanstackTableHeaderCellProps<TData>): JSX.Element {
  const { column } = header;
  const meta = column.columnDef.meta;
  const align = meta?.headerAlign ?? meta?.align ?? 'left';
  const locale = useTanstackTableLocale();

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const canSort = column.getCanSort();
  const canFilter = column.getCanFilter();
  const canPin = column.getCanPin();
  const canGroup = column.getCanGroup();
  const isFiltered = column.getIsFiltered();
  const sortDirection = column.getIsSorted();
  const hasMenu = canPin || canGroup;

  return (
    <TableCell
      align={toMuiAlign(align)}
      sortDirection={sortDirection || undefined}
      colSpan={header.colSpan}
      style={{
        ...getColumnSizingStyle(column, Boolean(hasGrowColumn)),
        ...getPinningStyles(column),
      }}
      sx={{ backgroundColor: 'background.paper', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {header.isPlaceholder ? null : (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ justifyContent: toFlexJustify(align) }}
        >
          {canSort ? (
            <TableSortLabel
              active={Boolean(sortDirection)}
              direction={sortDirection || 'asc'}
              onClick={column.getToggleSortingHandler()}
            >
              {flexRender(column.columnDef.header, header.getContext())}
            </TableSortLabel>
          ) : (
            <Box component="span">{flexRender(column.columnDef.header, header.getContext())}</Box>
          )}

          {canFilter && (
            <Tooltip title={locale.filter}>
              <IconButton
                size="small"
                onClick={(e: React.MouseEvent<HTMLElement>): void =>
                  setFilterAnchor(e.currentTarget)
                }
              >
                <Badge color="primary" variant="dot" invisible={!isFiltered}>
                  <FilterListIcon fontSize="inherit" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {hasMenu && (
            <IconButton
              size="small"
              onClick={(e: React.MouseEvent<HTMLElement>): void => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon fontSize="inherit" />
            </IconButton>
          )}
        </Stack>
      )}

      {canFilter && (
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={(): void => setFilterAnchor(null)}
        >
          <ColumnFilter column={column} facet={facets?.[column.id]} />
          {/*
            An array rather than a fragment: `Menu` walks its children to wire up
            keyboard navigation and cannot see through a fragment, which it warns
            about. Same shape as the pin entries in the menu below.
          */}
          {isFiltered && [
            <Divider key="clear-filter-divider" />,
            <MenuItem
              key="clear-filter"
              onClick={(): void => {
                column.setFilterValue(undefined);
                setFilterAnchor(null);
              }}
            >
              <ClearIcon fontSize="small" sx={{ mr: 1 }} /> {locale.clearFilter}
            </MenuItem>,
          ]}
        </Menu>
      )}

      {hasMenu && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={(): void => setMenuAnchor(null)}
        >
          {canGroup && (
            <MenuItem
              onClick={(): void => {
                column.getToggleGroupingHandler()();
                setMenuAnchor(null);
              }}
            >
              <WorkspacesIcon fontSize="small" sx={{ mr: 1 }} />
              {column.getIsGrouped() ? locale.ungroup : locale.groupByColumn}
            </MenuItem>
          )}
          {canPin && [
            <MenuItem
              key="pin-left"
              selected={column.getIsPinned() === 'left'}
              onClick={(): void => {
                column.pin(column.getIsPinned() === 'left' ? false : 'left');
                setMenuAnchor(null);
              }}
            >
              <PushPinIcon fontSize="small" sx={{ mr: 1, transform: 'rotate(-45deg)' }} />{' '}
              {locale.pinLeft}
            </MenuItem>,
            <MenuItem
              key="pin-right"
              selected={column.getIsPinned() === 'right'}
              onClick={(): void => {
                column.pin(column.getIsPinned() === 'right' ? false : 'right');
                setMenuAnchor(null);
              }}
            >
              <PushPinIcon fontSize="small" sx={{ mr: 1, transform: 'rotate(45deg)' }} />{' '}
              {locale.pinRight}
            </MenuItem>,
            column.getIsPinned() ? (
              <MenuItem
                key="unpin"
                onClick={(): void => {
                  column.pin(false);
                  setMenuAnchor(null);
                }}
              >
                <PushPinOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> {locale.unpin}
              </MenuItem>
            ) : null,
          ]}
        </Menu>
      )}
    </TableCell>
  );
}

export default TanstackTableHeaderCell as <TData>(
  props: TanstackTableHeaderCellProps<TData>
) => JSX.Element;
